import cliSpinners from 'cli-spinners';
import { useCliSpinner } from '../../hooks/use-cli-spinner';
import { cn } from '../../utils';

type LoadingSpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

/** All available spinner variants from cli-spinners */
export type LoadingSpinnerVariant = keyof typeof cliSpinners;

/** All available spinner variant names */
export const SPINNER_VARIANTS = Object.keys(cliSpinners) as LoadingSpinnerVariant[];

/** Get spinner metadata (frames, interval) for a variant */
export function getSpinnerMeta(variant: LoadingSpinnerVariant) {
  return cliSpinners[variant];
}

interface LoadingSpinnerProps {
  /** Size of the spinner. Defaults to 'md' */
  size?: LoadingSpinnerSize;
  /** Spinner variant from cli-spinners. Defaults to 'dots' */
  variant?: LoadingSpinnerVariant;
  /** Additional CSS classes */
  className?: string;
  /** Whether the spinner is active. Defaults to true */
  isActive?: boolean;
}

const sizeStyles: Record<LoadingSpinnerSize, string> = {
  xs: 'text-label-md',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-2xl',
  '2xl': 'text-4xl',
  '3xl': 'text-6xl',
  '4xl': 'text-8xl',
};

/**
 * Centralized loading spinner component using cli-spinners.
 * Use this component for all loading states across the app.
 *
 * @example
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" />
 * <LoadingSpinner variant="moon" />
 * <LoadingSpinner size="sm" variant="dots2" className="text-ds-blue-500" />
 */
export function LoadingSpinner({
  size = 'md',
  variant = 'dots',
  className,
  isActive = true,
}: LoadingSpinnerProps) {
  const spinner = cliSpinners[variant];
  const frame = useCliSpinner({ spinner, isActive });

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center text-muted-foreground',
        sizeStyles[size],
        className
      )}
      role="status"
      aria-label="Loading"
    >
      {frame}
    </span>
  );
}
